import React, { useEffect, useRef } from "react";

import clsx from "clsx";

import type { ButtonProps } from "@busbud/horizon";
import { Button, ButtonIcon, UnstyledButton } from "@busbud/horizon";

import { button_links_class } from "@app/components/button-link/button-link-constants";

import { addClickListener } from "./button-link-script";

export type ButtonLinkProps = {
  className?: string;
  tracking?:
    | { action: string; category?: string; details?: unknown }
    | undefined;
  href: string;
  icon?: boolean;
  new_tab?: boolean;
  unStyled?: boolean;
} & ButtonProps;

/**
 * Use this component instead of a normal link with a nofollow attribute, to prevent it into leaking to google bot.
 *
 * @param root0
 * @param root0.children
 * @param root0.className
 * @param root0.href
 * @param root0.tracking
 * @param root0.icon
 * @param root0.new_tab
 * @param root0.unStyled
 * @see {https://github.com/busbud/public-website/issues/8458}
 * @see {https://github.com/busbud/public-website/issues/16359}
 */
export const ButtonLink = ({
  children,
  className,
  href,
  tracking,
  icon,
  new_tab,
  unStyled,
  ...props
}: React.PropsWithChildren<ButtonLinkProps>) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref?.current) {
      addClickListener(ref.current);
    }
  }, []);

  const tracking_details = {
    ...(tracking?.details
      ? Object.fromEntries(Object.entries(tracking.details))
      : {}),
    ...(tracking?.category ? { link_target: tracking.category } : {})
  };

  const details =
    Object.keys(tracking_details).length > 0
      ? JSON.stringify(tracking_details)
      : undefined;

  if (unStyled) {
    return (
      <UnstyledButton
        ref={ref}
        data-href={href}
        data-tracking-action={tracking?.action}
        data-tracking-details={details}
        data-new-tab={new_tab}
        className={clsx(className, button_links_class)}
        {...props}
      >
        {children}
      </UnstyledButton>
    );
  }

  if (icon) {
    const { iconStart, ...icon_button_props } = props;
    return (
      <ButtonIcon
        data-href={href}
        data-tracking-action={tracking?.action}
        data-tracking-details={details}
        className={clsx(className, button_links_class)}
        {...icon_button_props}
      >
        {children}
      </ButtonIcon>
    );
  }
  return (
    <Button
      ref={ref}
      data-href={href}
      data-tracking-action={tracking?.action}
      data-tracking-details={details}
      data-new-tab={new_tab}
      className={clsx(className, button_links_class)}
      {...props}
    >
      {children}
    </Button>
  );
};
